<script setup lang="ts">
import type {SolutionCardItem} from '~/types/legacy'
import type {Populated} from '~/types/populated'
import {resolveMediaUrl} from '~/utils/relations'
import RichText from '~/components/common/rich-text.vue'
import {cx} from 'class-variance-authority'
import {ref, onMounted} from 'vue'

const props = defineProps<{
  cards: Populated<SolutionCardItem>[]
}>()

const standardClasses =
  'h-full rounded-lg relative sm:min-w-[320px] p-4 shadow-md text-sm bg-contain bordershadow-input-light relative border-4 border-white before:content-[""] before:absolute before:inset-[-12px] before:border-2 before:gray-100 before:rounded-lg before:box-border before:pointer-events-none'

const windowWidth = ref(0)

onMounted(() => {
  windowWidth.value = window.innerWidth
  window.addEventListener('resize', () => (windowWidth.value = window.innerWidth))
})

const getCardStyle = (card: SolutionCardItem) => ({
  backgroundImage: windowWidth.value > 640 ? `url(${resolveMediaUrl(card.image) ?? ''})` : 'none',
})
</script>

<template>
  <div class="rounded-lg bg-white">
    <div class="grid grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3">
      <div
        v-for="(card, index) in props.cards"
        :key="index"
        :style="getCardStyle(card)"
        :class="
          cx(
            standardClasses,
            card.large
              ? 'col-span-1 flex justify-end rounded bg-no-repeat sm:col-span-2 lg:col-span-2'
              : 'col-span-1 bg-white bg-left bg-no-repeat',
            card.fullWidth ? 'col-span-1 bg-right sm:col-span-2 lg:col-span-3' : 'bg-left',
            'relative border-4 border-white',
            'before:absolute before:inset-[-12px] before:rounded-lg before:border-2 before:border-gray-100 before:content-[\'\']'
          )
        "
      >
        <div class="leading-4" :class="card.large ? 'relative w-full md:w-2/3 lg:w-1/2' : 'relative'">
          <div class="flex items-center justify-start space-x-4 rounded-full p-2">
            <div class="rounded-lg bg-main-600 p-4 text-white">
              <img :src="resolveMediaUrl(card.icon)" />
            </div>
            <Heading size="small" class="text-md" :class="card.fullWidth ? 'w-[20%]' : 'w-2/4'">{{
              card.title
            }}</Heading>
          </div>

          <RichText
            :style="{fontSize: '16px', lineHeight: '24px'}"
            :class="card.fullWidth ? 'w-full md:w-1/2 lg:w-[350px]' : ''"
            :raw="card.list"
            class="font-normal text-gray-600"
          />
        </div>
      </div>
    </div>
  </div>
</template>
